<template>
  <fieldset class="segment-filter mb-3 p-3 border rounded bg-white">
    <d-row class="align-items-center">
      <d-col
        cols="12"
        md="4"
        class="mb-2 mb-md-0">
        <validation-provider
          :rules="isRequired ? 'required_true' : ''"
          :name="filterGroup + '-' + filterConfig.field"
          v-slot="{ errors }">
          <d-checkbox
            v-model="isFilterEnabled"
            toggle
            toggle-small
            :state="errors.length ? 'invalid' : null">
            <strong>{{ filterConfig.name }}</strong>
          </d-checkbox>
          <d-form-invalid-feedback>
            {{ errors[0] }}
          </d-form-invalid-feedback>
        </validation-provider>
      </d-col>

      <d-col
        cols="12"
        md="8"
        :class="{
          'segment-filter-values': true,
          'is-disabled': !isFilterLoadedAndEnabled
        }">
        <d-alert
          v-if="filterOptionsError"
          show
          theme="warning"
          class="my-0">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
        <template v-else>
          <div
            v-if="!isFilterLoaded"
            class="spinner spinner-border spinner-border-sm text-primary">
          </div>

          <div
            v-if="Array.isArray(filterConfig.type) && filterConfig.type.length > 1"
            class="form-group type-selection">
            <d-form-radio
              v-for="typeOption in filterConfig.type"
              :key="typeOption"
              class="mb-0"
              inline
              :name="filterConfig.field + '-selectedType'"
              :value="typeOption"
              v-model="selectedType"
              :disabled="!isFilterLoadedAndEnabled">
              {{ getTypeLabel(typeOption) }}
            </d-form-radio>
          </div>

          <div>
            <template v-if="selectedType === 'text'">
              <validation-provider
                :rules="isFilterLoadedAndEnabled ? 'required' : ''"
                :name="filterConfig.field"
                v-slot="{ errors }">
                <d-input
                  :id="filterConfig.field"
                  v-model="value"
                  :disabled="!isFilterLoadedAndEnabled"
                  :placeholder="$t('marketing.enterValue')"
                  :state="errors.length ? 'invalid' : null" />
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>
              </validation-provider>
            </template>

            <template v-if="selectedType === 'days_ago'">
              <validation-provider
                :rules="isFilterLoadedAndEnabled ? 'required|min_value:1' : ''"
                :name="filterConfig.field"
                v-slot="{ errors }">
                <d-input-group
                  :append="$t('marketing.daysAgo')"
                  :class="{'is-invalid': errors.length}">
                  <input
                    :class="{
                      'form-control': true,
                      'is-invalid': errors.length
                    }"
                    :invalid="errors.length ? true : null"
                    :id="filterConfig.field"
                    type="number"
                    min="1"
                    v-model="value"
                    :placeholder="$t('marketing.enterValue')"
                    :disabled="!isFilterLoadedAndEnabled"
                    style="max-width: 250px;" />
                </d-input-group>
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>
              </validation-provider>
            </template>

            <template v-else-if="selectedType === 'number_range'">
              <div class="d-flex align-items-center">
                <validation-provider
                  :rules="isFilterLoadedAndEnabled ? 'required|min_value:0' + (value.to !== null && value.to !== '' ? '|max_value:' + value.to : '') : ''"
                  :name="filterConfig.field + '-from'"
                  v-slot="{ errors }"
                  class="mb-auto"
                  style="width: calc(50% - 25px); max-width: 215px;">
                  <d-input-group
                    :prepend="$t('ui.from')"
                    :class="{'is-invalid': errors.length}">
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      :id="filterConfig.field + '-from'"
                      type="number"
                      min="0"
                      :max="value.to !== null && value.to !== '' ? value.to : null"
                      v-model="value.from"
                      :disabled="!isFilterLoadedAndEnabled" />
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
                <span class="mx-2 mt-2 mb-auto"> - </span>
                <validation-provider
                  :rules="isFilterLoadedAndEnabled ? 'required' + (value.from !== null && value.from !== '' ? '|min_value:' + value.from : '') : ''"
                  :name="filterConfig.field + '-to'"
                  v-slot="{ errors }"
                  class="mb-auto"
                  style="width: calc(50% - 25px); max-width: 215px;">
                  <d-input-group
                    :prepend="$t('ui.to')"
                    :class="{'is-invalid': errors.length}">
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      :id="filterConfig.field + '-to'"
                      type="number"
                      :min="value.from !== null && value.from !== '' ? value.from : null"
                      v-model="value.to"
                      :disabled="!isFilterLoadedAndEnabled" />
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>
            </template>

            <template v-else-if="selectedType === 'date_range'">
              <validation-provider
                :rules="isFilterLoadedAndEnabled ? 'required|required_date_range' : ''"
                :name="filterConfig.field"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-control is-date-range vs-wrap-text': true,
                    'is-invalid': errors.length,
                    'is-disabled': !isFilterLoadedAndEnabled
                  }">
                  <date-range-picker
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'DD-MM-YYYY',
                      'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
                      'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
                    }"
                    v-model="value"
                    :disabled="!isFilterLoadedAndEnabled"
                    :singleDatePicker="false"
                    :timePicker="false"
                    :showWeekNumbers="false"
                    :showDropdowns="false"
                    :autoApply="true"
                    :ranges="false">
                    <template
                      v-slot:input="picker"
                      style="min-width: 350px;">
                      {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
                    </template>
                  </date-range-picker>
                </div>
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>
              </validation-provider>
            </template>

            <template v-else-if="selectedType === 'multiselect'">
              <validation-provider
                :rules="isFilterLoadedAndEnabled ? 'required' : ''"
                :name="filterConfig.field"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-control is-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    v-model="value"
                    :options="filterOptions"
                    :placeholder="$t('marketing.choose')"
                    :disabled="!isFilterLoadedAndEnabled"
                    :reduce="item => item.id"
                    label="name"
                    :multiple="true">
                    <template slot="no-options">
                      {{ $t('ui.noOptionsAvailable') }}&hellip;
                    </template>
                  </v-select>
                </div>
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>
              </validation-provider>
            </template>

            <template v-else-if="selectedType === 'select'">
              <validation-provider
                :rules="isFilterLoadedAndEnabled ? 'required' : ''"
                :name="filterConfig.field"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-control is-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    v-model="value"
                    :options="filterOptions"
                    :placeholder="$t('marketing.choose')"
                    :disabled="!isFilterLoadedAndEnabled"
                    :reduce="item => item.id"
                    label="name"
                    :multiple="false">
                    <template slot="no-options">
                      {{ $t('ui.noOptionsAvailable') }}&hellip;
                    </template>
                  </v-select>
                </div>
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>
              </validation-provider>
            </template>

            <template v-else-if="selectedType === 'choice'">
              <d-checkbox
                id="filterConfig.field"
                v-model="value"
                :value="filterConfig.field === 'newsletter' ? 'subscribed' : true"
                :unchecked-value="filterConfig.field === 'newsletter' ? 'unsubscribed' : false"
                :disabled="!isFilterLoadedAndEnabled"
                toggle
                toggle-small>
                <template v-if="filterConfig.field === 'newsletter'">
                  {{ value === 'subscribed' ? $t('customer.newsletterStatus.subscribed') : $t('customer.newsletterStatus.unsubscribed') }}
                </template>
                <template v-else>
                  {{ value  ? $t('ui.yes') : $t('ui.no') }}
                </template>
              </d-checkbox>
            </template>
          </div>
        </template>
      </d-col>
    </d-row>
  </fieldset>
</template>

<script>
import FormUtils from '@/utils/FormUtils.js';
import orderStatuses from '@/data/order-statuses.js';
import CalendarUtils from '@/utils/CalendarUtils';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'segment-filter',
  props: {
    filterConfig: {
      required: true,
      type: Object
    },
    filterGroup: {
      required: true,
      type: String
    },
    forceRequiredFilter: {
      default: null,
      type: [Object, null]
    }
  },
  components: {
    'date-range-picker': DateRangePicker
  },
  computed: {
    isFilterLoaded () {
      return this.isFilterInitiated && this.filterOptionsLoaded;
    },
    isFilterLoadedAndEnabled () {
      return this.isFilterEnabled && this.isFilterLoaded;
    },
    filterOptionsEndpoint () {
      if (this.filterConfig.field === 'source') {
        return {
          endpoint: '/sources/items',
          where: {
            return: 'get'
          }
        }
      }
      if (this.filterConfig.field === 'region') {
        return {
          endpoint: '/customers/regions/items',
          where: {
            return: 'get'
          }
        }
      }

      if (this.filterConfig.field === 'tags') {
        return {
          endpoint: '/customers/tags/items',
          where: {
            return: 'get'
          }
        }
      }

      if (this.filterConfig.field === 'user') {
        return {
          endpoint: '/users/items',
          where: {
            return: 'get',
            type: ['admin', 'worker']
          }
        }
      }

      if (this.filterConfig.field === 'category') {
        return {
          endpoint: '/categories/items',
          where: {
            return: 'get'
          }
        }
      }

      return null;
    },
    isRequired () {
      if (!this.forceRequiredFilter) {
        return false;
      }

      if (this.forceRequiredFilter.group !== this.filterGroup) {
        return false;
      }

      if (this.forceRequiredFilter.field !== this.filterConfig.field) {
        return false;
      }

      console.log(4);
      return true;
    }
  },
  watch: {
    selectedType (newType) {
      this.initValueForType(newType);
      this.emitChange();
    },
    isFilterLoadedAndEnabled () {
      if (this.isFilterLoadedAndEnabled) {
        if (!this.value) {
          this.initValueForType(this.selectedType);
        }

        if (this.isAdditionalDataRequired && !this.filterOptions.length) {
          this.loadAdditionalData();
        }
      }
      this.emitChange();
    },
    value: {
      deep: true,
      handler() {
        this.emitChange();
      }
    }
  },
  data () {
    return {
      filterOptions: [],
      filterOptionsError: false,
      filterOptionsLoaded: true,
      isAdditionalDataRequired: false,
      isFilterEnabled: false,
      isFilterInitiated: false,
      selectedType: null,
      value: null
    }
  },
  mounted () {
    if (Array.isArray(this.filterConfig.type)) {
      this.selectedType = this.filterConfig.type[0];
    } else {
      this.selectedType = this.filterConfig.type;
    }

    this.initValueForType(this.selectedType);

    if (this.filterOptionsEndpoint && this.filterOptionsEndpoint.endpoint) {
      this.isAdditionalDataRequired = true;
    } else if (this.filterGroup === 'orders' && this.filterConfig.field === 'status') {
      this.filterOptions = this.getOrderStatusOptions();
    }

    this.isFilterInitiated = true;
  },
  methods: {
    loadAdditionalData () {
      this.filterOptionsLoaded = false;
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.filterOptionsEndpoint.endpoint,
        params: this.filterOptionsEndpoint.where,
        loadedKey: 'filterOptionsLoaded',
        outputKey: 'filterOptions',
        errorKey: 'filterOptionsError',
      });
    },
    getOrderStatusOptions () {
      const currentTranslations = orderStatuses[this.$i18n.locale];

      return Object.keys(currentTranslations).map(key => ({
        id: key,
        name: currentTranslations[key]
      }));
    },
    initValueForType (type) {
      if (type === 'number_range') {
        this.value = { from: null, to: null };
      } else if (type === 'date_range') {
        this.value = { startDate: null, endDate: null };
      } else if (type === 'multiselect') {
        this.value = [];
      } else if (type === 'choice') {
        this.value = false;
        if (this.filterConfig.field === 'newsletter') {
          this.value = 'unsubscribed';
        }
      } else {
        this.value = null;
      }
    },
    getTypeLabel (type) {
      const labels = {
        'date_range': this.$t('marketing.chooseOnCalendar'),
        'days_ago': this.$t('marketing.howManyDaysAgo'),
        'number_range': this.$t('marketing.numberRange')
      };
      return labels[type] || type;
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.from');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.to');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    emitChange () {
      if (this.isFilterLoadedAndEnabled) {
        let filterValue = this.value;
        if (this.selectedType === 'date_range') {
          filterValue = {
            from: this.value.startDate ? CalendarUtils.formatDateForPHP(this.value.startDate) : null,
            to: this.value.endDate ? CalendarUtils.formatDateForPHP(this.value.endDate) : null
          }
        }
        this.$emit('filter-changed', {
          field: this.filterConfig.field,
          group: this.filterGroup,
          isActive: true,
          name: this.filterConfig.name,
          type: this.selectedType,
          value: filterValue
        });
      } else {
        this.$emit('filter-changed', {
          field: this.filterConfig.field,
          group: this.filterGroup,
          isActive: false
        });
      }
    }
  }
}
</script>

<style lang="scss">
.segment-filter {
  &-values {
    opacity: 1;
    transition: opacity 0.2s;

    &.is-disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
  label {
    margin-bottom: 0;
  }

  .form-group.type-selection {
    display: flex;
    flex-grow: 0.5rem;
  }

  .is-date-range {
    max-width: 402px;
  }

  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -10px;
    margin-top: -10px;
    z-index: 1;
  }
}
</style>
